import { Box, Flex, Icon, Text } from '@everlywell/ui-kit';
import { ChatCircle } from '@phosphor-icons/react';
import { displayName as everywhereDisplayName } from 'components/Everywhere/helpers';
import React from 'react';
import { trackEva } from 'utils/eva';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import { EvaIcon } from '../../../../components/PageSections/DynamicQuizModal/BiomarkerInsights/EvaIcon';
import { useChatProductRecommendations } from '../../hooks/useChatProductRecommendations';
import type { ChatProductRecommendationsProps } from '../../types/components';
import { Chip } from '../primitives/ui/Chip';
import EvaProductCard from './EvaProductCard';
import Everlywell360ProductCard from './Everlywell360ProductCard';
import EverywhereSearchProductCard from './EverywhereSearchProductCard';

// Animation style for avatar with double bounce like a ball landing
// Delayed 150ms so product cards start appearing first
const getAvatarAnimationSx = () => ({
  opacity: 0,
  animation: `avatarBounce 0.9s ease-out 0.15s forwards`,
  '@keyframes avatarBounce': {
    '0%': {
      transform: 'translateY(-20px)',
      opacity: 0,
    },
    '20%': {
      transform: 'translateY(-10px)',
      opacity: 1,
    },
    '40%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
    '55%': {
      transform: 'translateY(-12px)',
      opacity: 1,
    },
    '70%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
    '80%': {
      transform: 'translateY(-4px)',
      opacity: 1,
    },
    '90%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transform: 'none',
    opacity: 1,
  },
});

// Product cards start appearing first (before Eva bounces at 250ms)
const getCardAnimationWithDelaySx = (index: number) => ({
  opacity: 0,
  animation: `productCardReveal 0.4s ease-out forwards`,
  animationDelay: `${100 + index * 100}ms`,
  '@keyframes productCardReveal': {
    '0%': {
      opacity: 0,
      transform: 'translateY(16px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
});

export default function ChatProductRecommendations({
  recommendations,
  showLogo = false,
  shouldAnimate = true,
  onLearnMore,
  onCardClick,
}: ChatProductRecommendationsProps): JSX.Element | null {
  const { productSlugs, visibleItems, hasEverlywell360 } =
    useChatProductRecommendations(recommendations);
  const { advertisedPromos } = useAdvertisedPromos();

  if (productSlugs.length === 0 && !hasEverlywell360) {
    return null;
  }

  return (
    <Flex
      width="100%"
      justifyContent="flex-start"
      flexDirection="column"
      alignItems="flex-start"
    >
      <Box
        maxWidth={800}
        width="100%"
        padding={{ base: '12px 0', md: '12px 16px' }}
      >
        {visibleItems.length > 0 ? (
          <Box
            display="grid"
            gridTemplateColumns={
              visibleItems.length === 1
                ? ['1fr', '340px']
                : ['1fr', 'repeat(2, 1fr)']
            }
            gap="12px"
            width="100%"
          >
            {visibleItems.map((item, idx) => (
              <Box
                key={item.slug}
                sx={
                  shouldAnimate ? getCardAnimationWithDelaySx(idx) : undefined
                }
              >
                {item.kind === 'everywhere' ? (
                  <EverywhereSearchProductCard
                    test={item.test}
                    description={item.test.desc}
                    imageIndex={item.imageIndex}
                    experience="conversation"
                    onCardClick={onCardClick ?? (() => undefined)}
                  />
                ) : (
                  <EvaProductCard
                    product={item.product}
                    advertisedPromos={advertisedPromos}
                    experience="conversation"
                    onCardClick={onCardClick}
                  />
                )}
              </Box>
            ))}
          </Box>
        ) : null}

        {hasEverlywell360 ? (
          <Box
            marginTop={visibleItems.length > 0 ? 12 : '0'}
            sx={
              shouldAnimate
                ? getCardAnimationWithDelaySx(visibleItems.length)
                : undefined
            }
          >
            <Everlywell360ProductCard advertisedPromos={advertisedPromos} />
          </Box>
        ) : null}

        {onLearnMore && visibleItems.length > 0 ? (
          <Flex
            flexWrap="wrap"
            gap="8px"
            marginTop="16px"
            justifyContent="flex-end"
            sx={
              shouldAnimate
                ? getCardAnimationWithDelaySx(
                    visibleItems.length + (hasEverlywell360 ? 1 : 0),
                  )
                : undefined
            }
          >
            {visibleItems.map((item) => {
              const name =
                item.kind === 'everywhere'
                  ? everywhereDisplayName(item.test)
                  : item.product.displayName;
              return (
                <Chip
                  key={`learn-${item.slug}`}
                  onClick={() => {
                    trackEva('Clicked Learn More Chip', {
                      product_name: name,
                      product_slug: item.slug,
                      experience: 'conversation',
                    });
                    onLearnMore(name);
                  }}
                  height="40px"
                  paddingY="8px !important"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    gap="6px"
                    paddingLeft={3}
                    paddingRight={4}
                  >
                    <Icon
                      as={ChatCircle}
                      aria-hidden="true"
                      color="eggplant.base"
                    />
                    <Text
                      as="span"
                      fontFamily="DM Sans"
                      fontSize="md"
                      fontWeight="medium"
                      lineHeight="150%"
                      color="eggplant.base"
                    >
                      {visibleItems.length === 1
                        ? 'Learn more'
                        : `Learn more: ${name}`}
                    </Text>
                  </Flex>
                </Chip>
              );
            })}
          </Flex>
        ) : null}
      </Box>

      {showLogo ? (
        <Box
          display="flex"
          alignItems="flex-start"
          paddingTop={{ base: '16px' }}
          paddingRight="16px"
          flexShrink={0}
          sx={shouldAnimate ? getAvatarAnimationSx() : undefined}
          data-testid="eva-avatar"
        >
          <Box
            padding="4px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="56px"
            height="56px"
            minWidth="56px"
            minHeight="56px"
            flexShrink={0}
          >
            <Box
              backgroundColor="violet.lighter"
              borderRadius="full"
              padding={2}
              display="flex"
              alignItems="center"
            >
              <EvaIcon />
            </Box>
          </Box>
        </Box>
      ) : null}
    </Flex>
  );
}
